import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { collection, query, where, getDocs, addDoc, setDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { scrapeAndExtractAcceleratorInfo, extractAcceleratorInfoFromText } from '../../services/backend';
import AcceleratorList from './AcceleratorList';
import AcceleratorModal from './AcceleratorModal';
import AcceleratorStats from './AcceleratorStats';
import UpcomingDeadlines from './UpcomingDeadlines';
import { Plus, Search, BarChart3, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Demo data for accelerators
const demoAccelerator = {
  id: 'demo',
  name: 'Y Combinator',
  type: 'Seed Accelerator',
  location: 'San Francisco, CA',
  website: 'https://ycombinator.com',
  focus: 'AI, SaaS, Consumer Tech',
  stage: 'Seed',
  investment: '$500K',
  equity: '7%',
  duration: '3 months',
  batchSize: '200+ companies',
  successRate: '85%',
  portfolio: 'Stripe, Airbnb, Dropbox, Reddit',
  applicationDeadline: '2024-09-15',
  programStart: '2025-01-15',
  status: 'Researching',
  notes: 'Most prestigious accelerator, highly competitive',
  tier: 'A',
  isDemo: true,
  companyId: 'demo'
};

const AcceleratorTracker = () => {
  const { currentUser } = useAuth();
  const { selectedWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const selectedCompany = selectedWorkspace; // Global workspace = selected company
  const [accelerators, setAccelerators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAccelerator, setSelectedAccelerator] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  const [creating, setCreating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showStatsModal, setShowStatsModal] = useState(false);

  // Auto-dismiss success modal after 1 second
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  useEffect(() => {
    const openModal = () => setShowModal(true);
    window.addEventListener('openAddAcceleratorModal', openModal);
    return () => window.removeEventListener('openAddAcceleratorModal', openModal);
  }, []);

  const [newAccelerator, setNewAccelerator] = useState({
    name: '',
    type: '',
    location: '',
    website: '',
    focus: '',
    stage: '',
    investment: '',
    equity: '',
    duration: '',
    batchSize: '',
    successRate: '',
    portfolio: '',
    applicationDeadline: '',
    programStart: '',
    status: 'Researching',
    notes: '',
    tier: 'B',
    userId: '',
    companyId: '',
  });

  // Load accelerators for current workspace (global selection from sidebar)
  useEffect(() => {
    if (!currentUser || !selectedCompany) return;
    const fetchAccelerators = async () => {
      setLoading(true);
      try {
        const acceleratorsRef = collection(db, 'accelerators');
        const q = query(acceleratorsRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
        const querySnapshot = await getDocs(q);
        const acceleratorsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAccelerators([demoAccelerator, ...acceleratorsData]);
      } catch (error) {
        console.error('Error fetching accelerators:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAccelerators();
  }, [currentUser, selectedCompany]);

  const handleAddAccelerator = async () => {
    if (!selectedCompany) {
      alert('Please select a company first');
      return;
    }
    if (selectedCompany.isDemo) {
      alert('Cannot add accelerators to demo company. Please select a real company first.');
      return;
    }
    setCreating(true);
    try {
      const acceleratorData = { ...newAccelerator, userId: currentUser.uid, companyId: selectedCompany.id };
      const docRef = await addDoc(collection(db, 'accelerators'), acceleratorData);
      setShowModal(false);
      setNewAccelerator({
        name: '', type: '', location: '', website: '', focus: '', stage: '', investment: '', equity: '', duration: '', batchSize: '', successRate: '', portfolio: '', applicationDeadline: '', programStart: '', status: 'Researching', notes: '', tier: 'B', userId: '', companyId: ''
      });
      
      setSuccessMessage('Accelerator added successfully!');
      setShowSuccessModal(true);
      
      // Refresh list
      const acceleratorsRef = collection(db, 'accelerators');
      const q = query(acceleratorsRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
      const querySnapshot = await getDocs(q);
      const acceleratorsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAccelerators([demoAccelerator, ...acceleratorsData]);
    } catch (error) {
      console.error('Error adding accelerator:', error);
    } finally {
      setCreating(false);
    }
  };

  const updateAccelerator = async (accelerator) => {
    try {
      if (!accelerator.isDemo && accelerator.id) {
        const { id, ...data } = accelerator;
        await setDoc(doc(db, 'accelerators', id), data);
        // Refresh list
        const acceleratorsRef = collection(db, 'accelerators');
        const q = query(acceleratorsRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
        const querySnapshot = await getDocs(q);
        const acceleratorsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAccelerators([demoAccelerator, ...acceleratorsData]);
      }
    } catch (error) {
      console.error('Error updating accelerator:', error);
    }
  };

  const deleteAccelerator = async (id) => {
    if (id === 'demo') {
      alert('Cannot delete demo accelerator');
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'accelerators', id));
      
      // Refresh list
      const acceleratorsRef = collection(db, 'accelerators');
      const q = query(acceleratorsRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
      const querySnapshot = await getDocs(q);
      const acceleratorsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAccelerators([demoAccelerator, ...acceleratorsData]);
      
      setSuccessMessage('Accelerator deleted successfully!');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error deleting accelerator:', error);
    }
  };

  const filteredAccelerators = accelerators.filter(accelerator => {
    const matchesSearch = accelerator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         accelerator.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         accelerator.focus.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = filterTier === 'all' || accelerator.tier === filterTier;
    const matchesStage = filterStage === 'all' || accelerator.stage === filterStage;
    return matchesSearch && matchesTier && matchesStage;
  });

  const statsData = {
    totalAccelerators: accelerators.length,
    tierAAccelerators: accelerators.filter(a => a.tier === 'A').length,
    applied: accelerators.filter(a => a.status === 'Applied').length,
    accepted: accelerators.filter(a => a.status === 'Accepted').length,
    rejected: accelerators.filter(a => a.status === 'Rejected').length,
    researching: accelerators.filter(a => a.status === 'Researching').length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-purple-800 py-4 sm:py-8 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Action Button - Add Accelerator hidden on mobile */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto mb-6">
          <button
            onClick={() => setShowModal(true)}
            disabled={!selectedCompany || selectedCompany.isDemo}
            className="hidden sm:inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={20} className="mr-2" />
            Add Accelerator
          </button>
          {/* Stats button - mobile only */}
          <button
            onClick={() => setShowStatsModal(true)}
            className="sm:hidden inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-white/20 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 transition-all duration-200 w-full"
          >
            <BarChart3 size={20} className="mr-2" />
            Stats
          </button>
        </div>

        {/* Stats - desktop inline; mobile via modal */}
        <div className="hidden sm:block mb-4">
          <AcceleratorStats statsData={statsData} />
        </div>
        {/* Stats Modal - mobile */}
        {showStatsModal && (
          <div className="sm:hidden fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowStatsModal(false)}>
            <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Accelerator Stats</h3>
                <button onClick={() => setShowStatsModal(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Close">
                  <X size={20} />
                </button>
              </div>
              <AcceleratorStats statsData={statsData} />
            </div>
          </div>
        )}

        {/* Upcoming Deadlines */}
        <UpcomingDeadlines accelerators={filteredAccelerators} />

        {/* Search - just above list */}
        <div className="mb-4">
          <div className="flex items-center bg-white/95 rounded-xl shadow-md px-3 py-2.5 sm:px-4 sm:py-2 w-full min-h-[44px] sm:min-h-0">
            <Search size={20} className="text-gray-500 mr-2 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search accelerators by name, type, or focus..."
              className="flex-1 min-w-0 bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 placeholder-gray-400 text-base sm:text-sm"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 sm:gap-4 mb-6">
          <select
            value={filterTier}
            onChange={e => setFilterTier(e.target.value)}
            className="bg-white/90 rounded-lg px-3 py-2.5 sm:py-2 text-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] sm:min-h-0 w-full sm:w-auto"
          >
            <option value="all">All Tiers</option>
            <option value="A">Tier A</option>
            <option value="B">Tier B</option>
            <option value="C">Tier C</option>
          </select>
          <select
            value={filterStage}
            onChange={e => setFilterStage(e.target.value)}
            className="bg-white/90 rounded-lg px-3 py-2.5 sm:py-2 text-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] sm:min-h-0 w-full sm:w-auto"
          >
            <option value="all">All Stages</option>
            <option value="Pre-seed">Pre-seed</option>
            <option value="Seed">Seed</option>
            <option value="Series A">Series A</option>
            <option value="Series B">Series B</option>
          </select>
        </div>

        {/* Accelerator List */}
        <AcceleratorList
          accelerators={filteredAccelerators}
          onAcceleratorClick={setSelectedAccelerator}
          onDelete={deleteAccelerator}
        />

        {/* Add/Edit Modal */}
        {showModal && (
          <AcceleratorModal
            accelerator={newAccelerator}
            setAccelerator={setNewAccelerator}
            onSave={handleAddAccelerator}
            onClose={() => setShowModal(false)}
            creating={creating}
            isDemo={selectedCompany?.isDemo}
          />
        )}

        {/* Edit Modal */}
        {selectedAccelerator && (
          <AcceleratorModal
            accelerator={selectedAccelerator}
            setAccelerator={setSelectedAccelerator}
            onSave={async () => {
              await updateAccelerator(selectedAccelerator);
              setSelectedAccelerator(null);
            }}
            onClose={() => setSelectedAccelerator(null)}
            creating={false}
            isDemo={selectedAccelerator.isDemo}
            onDelete={() => {
              setShowDeleteModal({ type: 'accelerator', id: selectedAccelerator.id });
              setSelectedAccelerator(null);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowDeleteModal(null)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-xs shadow-2xl relative mx-2 sm:mx-0" onClick={e => e.stopPropagation()}>
              <h2 className="text-lg font-bold mb-4">Are you sure?</h2>
              <p className="mb-6">This will permanently delete this accelerator.</p>
              <div className="flex gap-2">
                <button onClick={() => setShowDeleteModal(null)} className="flex-1 bg-gray-200 text-gray-700 font-bold py-2 rounded-xl">Cancel</button>
                <button onClick={async () => {
                  await deleteAccelerator(showDeleteModal.id);
                  setShowDeleteModal(null);
                }} className="flex-1 bg-red-500 text-white font-bold py-2 rounded-xl hover:bg-red-600">Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setShowSuccessModal(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl relative mx-2 sm:mx-0" onClick={e => e.stopPropagation()}>
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Success!</h3>
                <p className="text-sm text-gray-600 mb-6">{successMessage}</p>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-2 rounded-xl shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceleratorTracker; 